import { ChangeDetectorRef, Directive, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';
import { Category, ICategoryDataV2 } from '@df/catalog/category/category';
import { EdependencyLevel } from '@df/catalog/model';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { TimeoutService } from '@df/ng/timeout.service';
import { IAddToBasketData } from '@df/session/api/basket/basket';
import { BasketService } from '@df/session/api/basket/basket.service';
import { Wishlist } from '@df/session/api/wishlist/wishlist';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import { copyToClipboard } from '@df/util/fn/copy-to-clipboard';
import { ICmsModalService } from '@icms/client/assets/modal/icms-modal.service';
import { LocalProduct } from 'projects/app-default/src/app/catalog/model/product/product.decorator';
import { NotificationService } from 'projects/app-default/src/app/ui/notification/notification.service';
import { Subscription } from 'rxjs';
import { isOutOfStock } from '../../../../listing/filter/stock/filter-stock';
import { ProductListing } from '../../../../listing/product-listing.decorator';
import { NBuilderConfig } from '../builder.config';
import { NBuilderProductsSetup } from './builder-products-setup.config';
import { IFocusData, IOptionImageData } from './gallery/builder-gallery.component';
import { NBuilderProductPreviewModal } from './product-preview/builder-product-preview-modal.component';

namespace NTinyUrl {
    export const API_KEY = 'C4kDwD6Oz6C9jClD7HAFsoTknO3OqupY3wyeKaxQGc9VwMiXlDLj9smitEtD';

    export const BASE_URL = 'https://api.tinyurl.com/';
}

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class BuilderProductsSetupComponentDef implements OnInit, OnDestroy {
    @Input()
    abstract content: NBuilderConfig.IPageData;
    @Input()
    abstract storageData: NBuilderConfig.IStorageData;

    protected abstract useGA4: boolean;
    abstract get currentOption();
    abstract get leftSectionOption(): NBuilderProductsSetup.IOptionElementData[];
    abstract get additionalHorseStep(): NBuilderProductsSetup.IAdditionalHorseStep | undefined;
    abstract get isAdditionalStepState(): boolean;
    abstract get canShowToogle(): boolean;
    abstract get horseImageData(): IOptionImageData;
    abstract get riderImageData(): IOptionImageData;
    abstract get baseGalleryPosition(): IFocusData;
    abstract get displayAltImage(): boolean;

    // filter products by section option and remove options without products
    protected abstract initOptionElements();
    protected abstract undo();
    protected abstract reset();
    protected abstract changeToggleState(state);
    protected abstract selectAdditionalOption(name: string);
    protected abstract isSelectedAdditionalOption(name: string);
    private subscriptions = new Subscription();
    protected basketRequest!: IAddToBasketData[];
    protected totalPrice?: number;
    protected hideImageOnToggle: boolean = false;
    protected showCopyNotification?: boolean;
    protected showWishlistStatus = new Status();
    protected copyStatus = new Status();
    protected clickedProductId?: number;
    protected clickedProductStatus = new Status();
    protected category: Category<ICategoryDataV2> | undefined;
    private scrollToService = inject(ScrollToService);
    protected toggleOptions: NBuilderProductsSetup.IToggleOption[] = [
        { label: 'Rider', value: NBuilderProductsSetup.EToggleOptions.rider },
        { label: 'Horse', value: NBuilderProductsSetup.EToggleOptions.horse }
    ];

    private catalogUiConfig = inject(NCatalogUi.OPTIONS);
    private notificationService = inject(NotificationService);

    @ViewChild('gridContainer') private gridContainer!: ElementRef;

    constructor(
        protected app: App,
        protected changeDetectorRef: ChangeDetectorRef,
        readonly cmsModalService: ICmsModalService,
        protected basketService: BasketService,
        private timeoutService: TimeoutService,
        private requestService: RequestService,
        private wishlist: Wishlist // need to register wishlist in customer
    ) {}
    ngOnInit(): void {
        this.category = this.app.resources.category.getExisting(this.content.categoryId || 0) as Category<ICategoryDataV2>;
        this.initOptionElements();
        if (!this.storageData.selection) {
            this.storageData.selection = {};
        }
        this.updateBasketRequest();
        this.changeDetectorRef.detectChanges();
        this.subscriptions.add(
            this.app.events.on(NBuilderConfig.updateStorageEvent).subscribe(() => {
                this.updateBasketRequest();
                this.changeDetectorRef.detectChanges();
            })
        );
        this.subscriptions.add(this.app.client.obs.breakpoint.subscribe(() => this.changeDetectorRef.detectChanges()));
    }
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
    protected changeCurrentOption(optionId: number): void {
        if (this.storageData.currentSectionOptionId !== optionId && this.storageData.history) {
            this.storageData.history.prevSelectedSection = this.storageData.currentSectionOptionId;
            this.storageData.currentSectionOptionId = optionId;
            this.updateStorage();
            this.scrollGridToTop();
        }
    }
    protected sortBycategory(a: Product, b: Product): number {
        const indexA = this.category?.data?.products.indexOf(a.id);
        const indexB = this.category?.data?.products.indexOf(b.id);

        // If both elements are not found in the reference array, maintain their original order
        if (indexA === -1 && indexB === -1) {
            return 0;
        }
        // If only element A is not found in the reference array, move it to the end
        else if (indexA === -1) {
            return 1;
        }
        // If only element B is not found in the reference array, move it to the end
        else if (indexB === -1) {
            return -1;
        }
        // Otherwise, sort based on their index in the reference array
        else {
            return (indexA || 0) - (indexB || 0);
        }
    }
    protected async openProductPreview(product: Product<IProductDataV2>) {
        this.clickedProductId = product.id;
        this.clickedProductStatus.busy = true;
        if (this.app.client.isCms) {
            this.storageData.selection![this.storageData.currentSectionOptionId || ''] = {
                configurableSelection: undefined,
                productId: product.id,
                matchedProductId: undefined,
                productType: this.storageData['optionToggleState']
            };
            this.app.events.broadcast(NBuilderConfig.updateStorageEvent);
        } else {
            try {
                await product.load(3, { dependencyLevel: EdependencyLevel.details });
            } catch (error) {
                this.app.logger.error(`Loading product ${product.id} in product builder failed`, error);
                this.notificationService.add({ title: 'Loading product failed', copy: 'Please try again later or use another product' });
                return;
            }
            this.cmsModalService.open(undefined, NBuilderProductPreviewModal.id, undefined, {
                product: product,
                storageData: this.storageData,
                useGA4: this.useGA4
            });
        }
        this.clickedProductStatus.busy = false;
        this.changeDetectorRef.detectChanges();
    }
    protected getSectionOptionLabel(optionId: number) {
        return this.sectionOptionOption?.getOptionByValue(optionId)?.label;
    }
    protected isSectionOptionSelected(optionId: number): boolean {
        return !!this.storageData.selection?.[optionId];
    }
    protected isSelectedProduct(product: LocalProduct<IProductDataV2>): boolean {
        return (
            this.storageData.selection?.[this.storageData.currentSectionOptionId || '']?.productId === product?.id ||
            this.storageData.selection?.[this.storageData.currentSectionOptionId || '']?.productId === product?.parent?.id
        );
    }
    protected unselectCurrentOption(): void {
        delete this.storageData.selection?.[this.storageData.currentSectionOptionId || ''];
        this.updateStorage();
    }
    protected async copyUrlToClipboard(): Promise<void> {
        this.copyStatus.busy = true;
        this.changeDetectorRef.detectChanges();
        const tinyUrl = await this.requestService.send(
            this.copyStatus,
            {
                path: 'https://api.tinyurl.com/create',
                requestOptions: {
                    method: 'POST',
                    endpoint: NTinyUrl.BASE_URL + 'create',
                    params: {
                        api_token: NTinyUrl.API_KEY
                    }
                },
                data: JSON.stringify({
                    url: window.location.href
                })
            },
            undefined,
            () => {
                this.copyStatus.busy = false;
                this.changeDetectorRef.detectChanges();
            }
        );
        this.copyStatus.busy = false;
        if (tinyUrl?.tiny_url) {
            copyToClipboard(tinyUrl.tiny_url);
            this.showCopyNotification = true;
            this.changeDetectorRef.detectChanges();
            this.timeoutService.setTimeout(() => {
                this.showCopyNotification = false;
                this.changeDetectorRef.detectChanges();
            }, 5000);
        }
    }
    protected async addToWishlist(): Promise<void> {
        this.showWishlistStatus.busy = true;
        this.changeDetectorRef.detectChanges();
        const items = this.basketRequest
            .filter(item => !this.app.customer.wishlist.getItemsByProduct(item.id)?.length)
            .map(item => item.id);
        await this.app.customer.wishlist.add(new Status(), {
            product: items
        });
        this.showWishlistStatus.busy = false;
        this.showWishlistStatus.success = 'Added';
        this.changeDetectorRef.detectChanges();
        this.timeoutService.setTimeout(() => {
            this.showWishlistStatus = new Status();
            this.changeDetectorRef.detectChanges();
        }, 5000);
    }
    protected updateStorage(): void {
        this.app.events.broadcast(NBuilderConfig.updateStorageEvent);
    }
    protected findImage(options: NBuilderConfig.ISetupElementData[], optionName: string): { image: string; altImage?: string } {
        const configuration = options.find(o => o.name === optionName);
        return { image: configuration?.imageConfiguration || '', altImage: configuration?.alternativeImageConfiguration };
    }
    protected prepareListings(
        sectionElements: NBuilderProductsSetup.ILeftSectionElement[],
        getProducts: (data: NBuilderConfig.IStorageData, sectionOptionId?: number) => LocalProduct<IProductDataV2>[]
    ): NBuilderProductsSetup.ILeftSectionElement[] {
        return sectionElements
            ?.map(optionElement => {
                const products = getProducts(this.storageData, optionElement.sectionOption)?.filter(p => !isOutOfStock(p as LocalProduct));
                const listing = new ProductListing({}, products);
                listing.setAttributeFilters(optionElement.filterAttr || []);
                listing.setSortOptions(this.catalogUiConfig.listing.category.sort || []);
                // Subscribe for detect changes on filters change
                this.subscriptions.add(listing.subscribe(() => this.changeDetectorRef.detectChanges()));
                return {
                    ...optionElement,
                    listing: listing
                };
            })
            .filter(option => option.listing.models.length);
    }
    protected scrollAfterBasketRequestUpdate(): void {
        if (this.app.client.sizeId < 2) {
            // need timeout couse scroll is blocked when modal is open and we want animation of scroll
            this.timeoutService.setTimeout(() => {
                this.scrollToTop();
            }, 500);
        }
    }

    protected updateBasketRequest(): void {
        // need timeout couse scroll is blocked when modal is open and we want animation of scroll
        this.timeoutService.setTimeout(() => {
            if (this.app.client.sizeId < 2) {
                this.scrollToTop();
            } else {
                this.scrollGridToTop();
            }
        }, 500);

        this.basketRequest =
            this.basketService.normalizeAddToBasketData(
                (!!this.storageData.selection &&
                    Object.values(this.storageData.selection)?.map(option => ({
                        // if simple product isnt selected use configurable to calc price
                        id: option?.matchedProductId || option?.productId,
                        qty: 1
                    }))) ||
                    []
            ) || [];
        this.totalPrice = this.basketRequest.length ? this.basketService.calcTotalPrices(this.basketRequest)?.price : 0;
        this.scrollAfterBasketRequestUpdate();
    }
    protected trackByProductId(index: number, product: Product) {
        return product.id;
    }
    protected getGtmTrackData(product: Product<IProductDataV2>, index: number): Gtm.ITrackImpressionData | undefined {
        return this.useGA4
            ? ({
                  productId: product?.id,
                  position: index + 1,
                  list: `Outfit builder - ${optionLabelPipe(
                      this.storageData.currentSectionOptionId,
                      NBuilderProductsSetup.SECTION_OPTION_ATTR_ID
                  )}`
              } as Gtm.ITrackImpressionData)
            : undefined;
    }

    private scrollGridToTop(): void {
        if (this.gridContainer?.nativeElement) {
            this.gridContainer.nativeElement.scrollTop = 0;
        }
    }

    private scrollToTop(): void {
        this.scrollToService.scrollTo(0);
    }

    protected getGtmImpressionData(
        product: Product<IProductDataV2>,
        index: number,
        numberOfResults: number
    ): Gtm.ITrackImpressionData | undefined {
        const gtmData = this.getGtmTrackData(product, index);
        return (
            gtmData && {
                ...gtmData,
                numberOfResults: numberOfResults
            }
        );
    }
    protected getProducts(): LocalProduct<IProductDataV2>[] {
        const selectedProductId = this.storageData.selection?.[this.storageData.currentSectionOptionId || '']?.productId;
        // selected product can not exist in current listing cause of section option attr or horseType/riderType
        const selectedProduct =
            this.currentOption?.listing?.models?.find(p => p.id === selectedProductId) ||
            this.app.resources.product.getExisting(selectedProductId);
        return selectedProduct
            ? [selectedProduct, ...this.currentOption?.listing?.models?.filter(p => p.id !== selectedProductId)]
            : this.currentOption?.listing?.models;
    }
    get sectionOptionOption() {
        return this.app.resources.attribute.getExisting(NBuilderProductsSetup.SECTION_OPTION_ATTR_ID);
    }
    get toggleState() {
        return this.storageData.optionToggleState;
    }
    get displayNewLabelForToggle() {
        return false;
    }
}
